import cx from 'classnames';
import fuzzysort from 'fuzzysort';
import {ChangeEventHandler, FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {SearchLineIcon} from '~library/svg/SearchLineIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
import {pebble, tanzanite} from '~libSass/v2/_exports.sass';
import {findTimezoneObject} from './utils';

interface TimezoneSelectorContentProps {
  selectedTimezone: Timezone | null;
  timezones: TimezoneOption[];
  onTimezoneChange: (tz: Timezone | null) => void;
  open?: boolean;
}

export const TimezoneSelectorContent: FC<TimezoneSelectorContentProps> = ({
  selectedTimezone: tz,
  timezones,
  onTimezoneChange,
  open,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const displayedTimezones = useMemo(
    () =>
      fuzzysort
        .go(searchQuery, timezones, {
          keys: ['id', 'label', 'value'],
          all: true,
        })
        .map((result) => result.obj),
    [searchQuery, timezones]
  );

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setSearchQuery('');
  }, [open]);

  return (
    <div className="start-end-controller__timezone-selector">
      <Input
        placeholder="Search by city or name"
        startAdornment={<SearchLineIcon ratio={0.5} color={pebble} />}
        value={searchQuery}
        onChange={handleSearchChange}
        data-qa-id="search-time-zone"
      />
      <div
        className="start-end-controller__timezone-options"
        data-qa-id="start-end-controller__timezone-options"
      >
        {displayedTimezones.map((zone) => (
          <Button
            key={`${zone.id}-${zone.label}`}
            variant="unstyled-button"
            className={cx('start-end-controller__timezone-option', {
              selected: zone.label === tz?.timezone_display_name,
            })}
            onClick={() => {
              const tzObj = findTimezoneObject(zone.id);
              onTimezoneChange(tzObj ?? null);
            }}
            data-qa-id="start-end-controller__timezone-option"
            data-qa-value={zone.value}
            aria-label={zone.label}
          >
            {zone.label}
            {zone.label === tz?.timezone_display_name && (
              <CheckmarkIcon ratio={0.5} color={tanzanite} />
            )}
          </Button>
        ))}
      </div>
    </div>
  );
};
